<template>
  <div class="i-live-coach">
    <div class="pre-text">
      Unlock your full potential with personalized insights and tailored career paths. See how JobTest can guide you to
      your best future.
      <iBaseButton
        v-bind="buttonStyle"
        text="Unlock Your Future"
        :icon-url="iconArrowDown"
        icon-width="22px"
        @clicked="$emit('scrollToPaymentTable')"
      />
    </div>
    <div class="main">
      <img
        class="phone"
        :src="imageLiveCoach"
      />
      <div class="right">
        <div class="up">
          <div class="head">Supercharge your career with live career coaching</div>
          <div class="sub-text">
            JobTest starts by generating 5 personality insights and 10 career recommendations—but that's just the
            beginning. Like any great coach, JobTest is here to help you win in both life and career. The only limit is
            how big you're willing to dream.
          </div>
          <div class="grid">
            <div class="item"><img :src="icon1" />Weekly Career Coaching</div>
            <div class="item"><img :src="icon2" />Job Opening Finder</div>
            <div class="item"><img :src="icon3" />Career Exploration</div>
            <div class="item"><img :src="icon4" />Salary Coaching</div>
            <div class="item"><img :src="icon5" />Interview Preparation</div>
            <div class="item"><img :src="icon6" />LinkedIn Optimization</div>
            <div class="item"><img :src="icon7" />Resume Generation</div>
            <div class="item"><img :src="icon8" />Job Applications</div>
            <div class="item"><img :src="icon9" />2hrs of Live Coaching/Mo</div>
          </div>
        </div>
        <div class="button">
          <iBaseButton
            v-bind="buttonStyle"
            text="Unlock your future"
            :icon-url="iconArrowDown"
            icon-width="22px"
            @clicked="$emit('scrollToPaymentTable')"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue"
import { bluePurpleGradient, white } from "@/styles/colors"
import { breakpointXxs } from "@/styles/variables"

import useIsScreenSize from "@/composables/useIsScreenSize"

import iBaseButton from "@/components/base/BaseButton.vue"

// ASSETS
import imageLiveCoach from "@/assets/welcome/live-coach.svg"
import iconArrowDown from "@/assets/icons/icon-arrow-down-white.svg"
import icon1 from "@/assets/icons/icon-welcome-coach.svg"
import icon4 from "@/assets/icons/icon-welcome-salary.svg"
import icon8 from "@/assets/icons/icon-welcome-application.svg"
import icon7 from "@/assets/icons/icon-welcome-resume.svg"
import icon3 from "@/assets/icons/icon-welcome-career.svg"
import icon2 from "@/assets/icons/icon-welcome-finder.svg"
import icon5 from "@/assets/icons/icon-welcome-interview.svg"
import icon6 from "@/assets/icons/icon-welcome-linkedin.svg"
import icon9 from "@/assets/icons/icon-welcome-guidance.svg"

// EMITS
const emits = defineEmits(["scrollToPaymentTable"])

// COMPOSABLES
const isXxsOrLarger = useIsScreenSize(breakpointXxs)

// CONSTANTS
const buttonStyle = computed(() => {
  return {
    backgroundImage: bluePurpleGradient,
    borderRadius: "8px",
    fontWeight: "500",
    textColor: white,
    width: isXxsOrLarger.value ? "max-content" : "100%",
    padding: isXxsOrLarger.value ? "8px 70px" : "10px 25px",
    size: "medium"
  }
})
</script>

<style lang="scss" scoped>
.i-live-coach {
  @include flex(column, start, center, $gap: 40px);

  font-weight: 300;

  .pre-text {
    @include flex(column, center, center, $gap: 40px);
    @include text-xl;

    text-align: center;

    @media screen and (min-width: $breakpoint-md) {
      display: none;
    }
  }

  .main {
    @include flex(column, center, center, $gap: 40px);

    @media screen and (min-width: $breakpoint-md) {
      @include flex(row, start, stretch, $gap: 40px);
    }

    .phone {
      width: 100%;
      max-width: 350px;

      @media screen and (min-width: $breakpoint-md) {
        max-width: 250px;
      }

      @media screen and (min-width: $breakpoint-lg) {
        max-width: 400px;
      }
    }

    .right {
      @include flex(column, center, center, $gap: 40px);

      position: relative;

      @media screen and (min-width: $breakpoint-md) {
        @include flex(column, center, start);
      }

      .up {
        @include flex(column, center, center, $gap: 20px);

        text-align: center;

        @media screen and (min-width: $breakpoint-md) {
          @include flex(column, start, start, $gap: 25px);

          text-align: start;
        }

        @media screen and (min-width: $breakpoint-lg) {
          gap: 40px;
        }

        .head {
          @include display-xs;

          max-width: 600px;

          @media screen and (min-width: $breakpoint-md) {
            @include display-md;

            font-weight: 600;
          }

          @media screen and (min-width: $breakpoint-xl) {
            @include display-lg;

            font-weight: 600;
          }
        }

        .sub-text {
          @include text-lg;

          @media screen and (min-width: $breakpoint-md) {
            display: none;
          }
        }

        .grid {
          @include grid($cols: repeat(2, 1fr), $rows: repeat(4, 1fr), $align-items: center, $gap: 5px 10px);

          text-align: start;

          @media screen and (min-width: $breakpoint-xs) {
            gap: 6px 10px;
          }

          @media screen and (min-width: $breakpoint-md) {
            gap: 16px;
          }

          @media screen and (min-width: $breakpoint-xl) {
            @include grid($cols: repeat(3, 1fr), $rows: repeat(3, 1fr), $align-items: center, $gap: 35px 16px);
          }

          .item {
            @include flex(row, start, center, $gap: 6px);
            @include text-xs;

            @media screen and (min-width: $breakpoint-xs) {
              @include text-sm;

              gap: 10px;
            }

            @media screen and (min-width: $breakpoint-lg) {
              @include text-lg;
            }

            img {
              width: 20px;
              height: 20px;

              @media screen and (min-width: $breakpoint-xs) {
                width: 26px;
                height: 26px;
              }

              @media screen and (min-width: $breakpoint-sm) {
                width: 30px;
                height: 30px;
              }

              @media screen and (min-width: $breakpoint-lg) {
                width: 40px;
                height: 40px;
              }
            }
          }
        }
      }

      .button {
        @include flex(row, center, center);

        width: 100%;

        @media screen and (min-width: $breakpoint-md) {
          width: unset;
          position: absolute;
          bottom: -5px;
          left: 0;
        }

        @media screen and (min-width: $breakpoint-lg) {
          bottom: 20px;
        }
      }
    }
  }
}
</style>
